import React, { useMemo } from 'react';
import {
  DateRangeSelector,
  Dropdown,
  DropdownItem,
  DropdownList,
  ENUM_DATE_RANGE,
  Paginate,
  Input
} from '@cokitchen/cokitchen-components';
import { FilterAltIcon, CloseOutlineIcon } from 'cokitchen-icons';
import { FilterType } from '.';
import { DROP_DOWN_OPTIONS_TYPE, ENUM_PAYMENT_STATUS } from 'models/common.model';
import { ALL } from 'utils/constants';

import useDashboardContext from 'contexts/dashboard.context';

export type ExtendedRequestType = ENUM_PAYMENT_STATUS | typeof ALL;

type Props = {
  filters: FilterType;
  setFilters: (arg0: FilterType) => void;
  pageCount?: number;
};
const PaymentTrackerFilter = ({ filters, setFilters, pageCount = 1 }: Props) => {
  const { formVendorsList } = useDashboardContext();
  const STATUS_LIST: { text: string; value: ExtendedRequestType }[] = [
    {
      text: 'All Payments',
      value: ALL
    },
    {
      text: 'Requested',
      value: ENUM_PAYMENT_STATUS.REQUESTED
    },
    {
      text: 'Approved',
      value: ENUM_PAYMENT_STATUS.APPROVED
    },
    {
      text: 'Paid',
      value: ENUM_PAYMENT_STATUS.PAID
    },
    {
      text: 'Rejected',
      value: ENUM_PAYMENT_STATUS.REJECTED
    }
  ];

  const vendorsList = [
    { text: 'All vendors', value: ALL, name: 'All vendors', id: ALL },
    ...formVendorsList
  ];

  const handleFilter = (field: string, filter: ExtendedRequestType | DROP_DOWN_OPTIONS_TYPE) => {
    setFilters({ ...filters, [field]: filter, current_page: 1 });
  };

  const activeStatusFilter = useMemo(() => {
    return filters.status !== ALL ? filters.status : null;
  }, [filters.status]);

  const activeVendorFilter = useMemo(() => {
    return filters.vendor.value !== ALL ? filters.vendor.text : null;
  }, [filters.vendor]);

  const activeDateFilter = useMemo(() => {
    return filters.dateRange.range !== ENUM_DATE_RANGE.ALL ? filters.dateRange.range : null;
  }, [filters.dateRange]);

  const hasActiveFilters = useMemo(() => {
    return activeStatusFilter !== null || activeVendorFilter !== null || activeDateFilter !== null;
  }, [activeStatusFilter, activeVendorFilter, activeDateFilter]);

  const handleRemoveStatusFilter = () => {
    setFilters({ ...filters, status: ALL, current_page: 1 });
  };

  const handleRemoveVendorFilter = () => {
    setFilters({ ...filters, vendor: { text: 'All vendors', value: ALL }, current_page: 1 });
  };

  const handleRemoveDateFilter = () => {
    setFilters({
      ...filters,
      dateRange: { startDate: new Date(), endDate: new Date(), range: ENUM_DATE_RANGE.ALL },
      current_page: 1
    });
  };

  const handleSearchChange = (value: string) => {
    setFilters({
      search: value,
      status: ALL,
      vendor: { text: 'All vendors', value: ALL },
      dateRange: { startDate: new Date(), endDate: new Date(), range: ENUM_DATE_RANGE.ALL },
      current_page: 1
    });
  };

  return (
    <div>
      <div className='flex justify-between items-center'>
        <div className='flex gap-3 items-center'>
          <Dropdown
            hideOnClick
            hideArrow
            placement='bottom-start'
            content={
              <div className='border border-[#E5E6E6] rounded-[5px] text-xs leading-[19px] h-[36px] flex px-2 justify-between items-center w-[114px]  cursor-pointer'>
                <span
                  className={`text-[#5E6164] text-sm capitalize whitespace-nowrap max-w-[calc(100%-25px)] overflow-hidden text-ellipsis`}>
                  {filters?.status.toLowerCase()}
                  {filters.status === ALL ? ' payments' : ''}
                </span>
                <FilterAltIcon className={`${filters.status ? 'text-red-5' : 'text-[#5E6164]'}`} />
              </div>
            }>
            <DropdownList>
              {STATUS_LIST?.map(({ text, value }, i) => (
                <DropdownItem
                  className={`capitalize ${filters.status === value ? 'text-red-5' : ''}`}
                  key={i}
                  onClick={() => handleFilter('status', value)}>
                  {text}
                </DropdownItem>
              ))}
            </DropdownList>
          </Dropdown>
          <Dropdown
            hideOnClick
            hideArrow
            placement='bottom-start'
            content={
              <div className='border border-[#E5E6E6] rounded-[5px] text-xs leading-[19px] h-[36px] flex px-2 justify-between items-center w-[114px]  cursor-pointer'>
                <span
                  className={`text-[#5E6164] text-sm capitalize whitespace-nowrap max-w-[calc(100%-25px)] overflow-hidden text-ellipsis`}>
                  {filters.vendor.text} {filters.vendor.value === ALL ? ' vendors' : ''}
                </span>
                <FilterAltIcon
                  className={`${filters.vendor.value ? 'text-red-5' : 'text-[#5E6164]'}`}
                />
              </div>
            }>
            <DropdownList>
              {vendorsList?.map(({ name, id }, i) => (
                <DropdownItem
                  className={`capitalize ${filters.vendor.value === id ? 'text-red-5' : ''}`}
                  key={i}
                  onClick={() => handleFilter('vendor', { text: name, value: id })}>
                  {name}
                </DropdownItem>
              ))}
            </DropdownList>
          </Dropdown>

          <DateRangeSelector
            defaultRange={ENUM_DATE_RANGE.ALL}
            onChange={(v) => {
              setFilters({ ...filters, dateRange: { ...v.date, range: v.range } });
            }}
            className='text-[#5E6164] h-[36px] text-sm border-[#E5E6E6]'
            isInput
          />

          <Input
            type='search'
            className='w-[160px]'
            inputClass='h-[32px]'
            placeholder='Search code'
            value={filters.search ?? ''}
            onChange={(event) => handleSearchChange(event.target.value)}
          />
        </div>

        {pageCount > 1 && (
          <div>
            <Paginate
              pageCount={pageCount}
              pageRangeDisplayed={2}
              onPageChange={(e) => setFilters({ ...filters, current_page: e.selected + 1 })}
              forcePage={filters.current_page - 1}
            />
          </div>
        )}
      </div>

      {hasActiveFilters && (
        <div className='mt-2'>
          <ul className='flex flex-wrap gap-2'>
            {activeStatusFilter && (
              <li>
                <button
                  type='button'
                  onClick={handleRemoveStatusFilter}
                  className='inline-flex items-center gap-1 rounded-full bg-[#F2F2F3] px-2 py-1 text-xs text-[#2E3031] capitalize hover:bg-[#E5E6E6]'>
                  <span>{activeStatusFilter.toLowerCase()}</span>
                  <CloseOutlineIcon className='text-[#5E6164]' />
                </button>
              </li>
            )}
            {activeVendorFilter && (
              <li>
                <button
                  type='button'
                  onClick={handleRemoveVendorFilter}
                  className='inline-flex items-center gap-1 rounded-full bg-[#F2F2F3] px-2 py-1 text-xs text-[#2E3031] hover:bg-[#E5E6E6]'>
                  <span>{activeVendorFilter}</span>
                  <CloseOutlineIcon className='text-[#5E6164]' />
                </button>
              </li>
            )}
            {activeDateFilter && (
              <li>
                <button
                  type='button'
                  onClick={handleRemoveDateFilter}
                  className='inline-flex items-center gap-1 rounded-full bg-[#F2F2F3] px-2 py-1 text-xs text-[#2E3031] capitalize hover:bg-[#E5E6E6]'>
                  <span>{activeDateFilter.toLowerCase().replace('_', ' ')}</span>
                  <CloseOutlineIcon className='text-[#5E6164]' />
                </button>
              </li>
            )}
          </ul>
        </div>
      )}
    </div>
  );
};

export default PaymentTrackerFilter;
